import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Button, TextField,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  CircularProgress, Tooltip, Alert, Checkbox, Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import {
  subscribeSharedLists, createSharedList, updateSharedList, deleteSharedList,
} from '../../utils/firebase';
import { copyText, shareLink } from '../../utils/clipboard';
import { PROGRAM_META } from './programMeta';

const listUrl = (id) => `${window.location.origin}/list/${id}`;

export default function SharedListsDrawer({ open, onClose, user, personalPlaces, programPlaces }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingList, setEditingList] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    const unsub = subscribeSharedLists(user.uid, data => {
      setLists(data);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [open, user]);

  const handleShare = async (list) => {
    const result = await shareLink(`${list.name} — FoodList`, listUrl(list.id));
    if (result === 'copied') setSnackbar('Share link copied to clipboard');
    else if (result === 'failed') setSnackbar(`Copy failed — link: ${listUrl(list.id)}`);
  };

  const handleCreated = async (id) => {
    const ok = await copyText(listUrl(id));
    setSnackbar(ok ? 'List created — share link copied to clipboard' : 'List created');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this list? Anyone with the link will lose access.')) return;
    await deleteSharedList(id);
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 400 }, bgcolor: '#0d0d14', borderLeft: '1px solid rgba(255,255,255,0.08)' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>Shared Lists</Typography>
            <Typography variant="caption" color="text.disabled">
              Anyone with a link can view — no account needed
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          <Button fullWidth variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditingList(null); setCreateOpen(true); }}>
            Create New List
          </Button>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={28} /></Box>
          ) : lists.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 6, px: 3 }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>🔗</Typography>
              <Typography color="text.secondary" variant="body2">
                No lists yet. Pick your favourite places, create a list, and send the link to friends.
              </Typography>
            </Box>
          ) : (
            <List dense>
              {lists.map(list => (
                <ListItem key={list.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.04)', py: 1.5, pr: 14 }}>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={700}>{list.name}</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.disabled">
                        {list.places?.length || 0} place{list.places?.length !== 1 ? 's' : ''}
                        {list.description ? ` · ${list.description}` : ''}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Share link">
                      <IconButton size="small" sx={{ color: 'primary.main' }}
                        onClick={() => handleShare(list)}>
                        <LinkIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Preview">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        component="a" href={listUrl(list.id)} target="_blank" rel="noopener">
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={() => { setEditingList(list); setCreateOpen(true); }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" sx={{ color: 'text.disabled' }}
                        onClick={() => handleDelete(list.id)}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <CreateListDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        user={user}
        personalPlaces={personalPlaces}
        programPlaces={programPlaces}
        existingList={editingList}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

// Snapshot the fields a viewer needs into the list document itself. Firestore
// rules block strangers from reading foodPlaces, so shared lists must be
// self-contained rather than referencing personal docs by id.
function toSnapshot(place, type) {
  return {
    type,
    id: place.id,
    name: place.name || '',
    address: place.address || '',
    latitude: place.latitude ?? null,
    longitude: place.longitude ?? null,
    cuisine: place.cuisine || '',
    category: place.category || '',
    photoURL: place.photoURL || null,
    placeId: place.placeId || null,
    programs: place.programs || [],
    visited: place.visited || false,
    visitedRating: place.visitedRating ?? null,
    notes: place.remarks || place.notes || '',
    instagram: place.instagram || '',
  };
}

function CreateListDialog({ open, onClose, onCreated, user, personalPlaces, programPlaces, existingList }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setName(existingList?.name || '');
      setDescription(existingList?.description || '');
      setError('');
      setSearch('');
      if (existingList?.places) {
        setSelected(new Set(existingList.places.map(p => `${p.type}:${p.id}`)));
      } else {
        setSelected(new Set());
      }
    }
  }, [open, existingList]);

  const toggle = (type, id) => {
    const key = `${type}:${id}`;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allPlaces = [
    ...personalPlaces.map(p => ({ ...p, _type: 'personal' })),
    ...programPlaces.map(p => ({ ...p, _type: 'program' })),
  ];

  const filtered = allPlaces.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredSelected = filtered.length > 0 &&
    filtered.every(p => selected.has(`${p._type}:${p.id}`));

  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(p => {
        const key = `${p._type}:${p.id}`;
        if (allFilteredSelected) next.delete(key); else next.add(key);
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (selected.size === 0) { setError('Select at least one place.'); return; }
    setSaving(true); setError('');
    try {
      const byKey = new Map(allPlaces.map(p => [`${p._type}:${p.id}`, p]));
      const places = [...selected].flatMap(key => {
        const place = byKey.get(key);
        // Place may have been deleted since the list was created; keep the
        // previously saved snapshot if we still have it.
        if (!place) {
          const prev = existingList?.places?.find(p => `${p.type}:${p.id}` === key);
          return prev ? [prev] : [];
        }
        return [toSnapshot(place, place._type)];
      });
      const data = { name: name.trim(), description: description.trim(), places };
      if (existingList) {
        await updateSharedList(existingList.id, data);
      } else {
        const ref = await createSharedList(user.uid, data);
        onCreated?.(ref.id);
      }
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#16162a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, maxHeight: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700}>{existingList ? 'Edit List' : 'Create Shared List'}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="List name" value={name} onChange={e => setName(e.target.value)}
          fullWidth required size="small" placeholder="e.g. Date night spots, CBD lunch" autoFocus />
        <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} fullWidth size="small" />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              Select places ({selected.size} selected)
            </Typography>
            {filtered.length > 0 && (
              <Button size="small" startIcon={<PlaylistAddCheckIcon />} onClick={toggleAllFiltered}
                sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                {allFilteredSelected ? 'Deselect all' : `Select all${search ? ' matching' : ''}`}
              </Button>
            )}
          </Box>
          <TextField
            placeholder="Search places..." value={search} onChange={e => setSearch(e.target.value)}
            fullWidth size="small" sx={{ mb: 1 }}
            inputProps={{ autoComplete: 'off' }}
          />
          <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1 }}>
            {filtered.map(place => {
              const key = `${place._type}:${place.id}`;
              const isChecked = selected.has(key);
              return (
                <Box key={key}
                  onClick={() => toggle(place._type, place.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                    cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)',
                    bgcolor: isChecked ? 'rgba(255,112,67,0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                  }}>
                  <Checkbox size="small" checked={isChecked} readOnly sx={{ p: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap fontWeight={isChecked ? 600 : 400}>{place.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {place.cuisine && (
                        <Typography variant="caption" color="text.disabled">{place.cuisine}</Typography>
                      )}
                      {place._type === 'program' && (place.programs || []).map(p => (
                        <Chip key={p} label={PROGRAM_META[p]?.short || p} size="small"
                          sx={{ height: 14, fontSize: '0.55rem', bgcolor: PROGRAM_META[p]?.bg, color: PROGRAM_META[p]?.color }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {filtered.length === 0 && (
              <Typography color="text.disabled" variant="caption" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                No places found
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : existingList ? 'Save Changes' : 'Create & Copy Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
